
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Skill, SkillAdapter } from '../models/skill';
import { SkillsService } from '../services/skills.service';
import { Observable, catchError, finalize, map, of } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { HideAfterDirective } from '../directives/hide-after.directive';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AvatarComponent } from '../_utils/components/avatar/avatar.component';
import { ToastService } from '../services/toast.service';
import { PagedResponse, PaginationMetadata } from '../models/paged-response.model';
import { adaptPagedResponse } from '../operators/paged-response-operatros';
import { PaginationControlsComponent } from '../_utils/components/pagination-controls/pagination-controls.component';
import { PaginationBaseComponent } from '../_utils/pagination-base.abstract'; 


@Component({
  selector: 'app-skill-list',
  standalone: true,
  imports: [CommonModule,
            ConfirmDialogComponent, 
            FormsModule,
            HideAfterDirective,
            RouterLink,
            TranslatePipe,
            AvatarComponent,
            PaginationControlsComponent
          ],
  templateUrl: './skill-list.component.html',
  styleUrl: './skill-list.component.scss'
})
export class SkillListComponent extends PaginationBaseComponent {
  skills!: Skill[];
  skillToDelete!: Skill;
  skillToEdit: Skill | null = null;
  showConfirmDialog: boolean = false;
  ShowForm: boolean = false;
  isLoading = false;

  private skillsObservable!: Observable<Skill[]>;
  errorMessage: string | null = null;
  formErrorMessage: string | null = null;



  /**
   * Constructor that injects the SkillsService and SkillAdapter.
   * @param skillsService The SkillsService to use for fetching and manipulating skills.
   * @param skillAdapter The SkillAdapter to use for adapting Skills from the server to the client.
   */
  constructor(
    private skillsService: SkillsService,
    private skillAdapter: SkillAdapter,
    private toastService: ToastService
  ) { super(); }

  /**
   * Lifecycle hook that is called when the component is initialized.
   * Fetches all skills from the server and stores them in the component's
   * `skills` property.
   */
  ngOnInit(): void {
    this.fetchSkills();
  }
  /**
   * @inheritdoc
   */
  protected loadData(): void {
    this.fetchSkills();
  }

  /**
   * Retrieves all skills from the server and stores them in the component's
   * `skills` property. The skills are adapted to the `Skill` model using the
   * `SkillAdapter` before being stored.
   *
   * @remarks
   * This method is called once when the component is initialized.
   */
  private fetchSkills(): void {
    this.isLoading = true;

    // 1. Appel du service avec les paramètres de la pagination
    this.skillsService
      .getSkills(this.pagination.page, this.pagination.per_page)
      .pipe(
        // 1. ADAPTATION : L'opérateur personnalisé transforme PagedResponse<Skill> en PagedResponse<Skill[] adapté>
        adaptPagedResponse<Skill, Skill>(this.skillAdapter),
        // 2. EXTRACTION : Le map standard extrait les items et les métadonnées de l'objet PagedResponse
        map((response: PagedResponse<Skill>) => {
          this.pagination = response.pagination;
          // Retourne le tableau de Skills adapté
          return response.items;
        }),
        catchError((error: any) => {
          this.errorMessage = 'Error during skills fetch : ' + error.message;
          // Réinitialiser les métadonnées de pagination en cas d'erreur
          this.pagination = { ...this.pagination, total_items: 0, total_pages: 0, has_next: false, has_prev: false };
          return of([]);
        }),
        finalize(() => this.isLoading = false)
      ).subscribe((res: Skill[]) => {
        this.skills = res;
      });
  }

  confirmDelete(skill: Skill): void {
    this.skillToDelete = skill;
    this.showConfirmDialog = true;
  }

  handleConfirm(result: boolean): void {
    if (result) {
      this.deleteSkill(this.skillToDelete);
    }
    this.showConfirmDialog = false;
  }

  editSkill(skill: Skill): void {
    this.skillToEdit = { ...skill };
    this.ShowForm = true;
  }

  cancelEdit(): void {
    this.skillToEdit = null;
    this.ShowForm = false;
  }

  addNewSkill(): void {
    this.skillToEdit = new Skill(0, '', '', new Date(), new Date);
    this.ShowForm = true;
  }

  /**
   * Handles the submission of the skill form for both adding and updating skills.
   * If `skillToEdit` has an ID, it updates the existing skill; otherwise, it adds a new skill.
   */
  onSubmit(): void {
    if (this.skillToEdit) {
      if (this.skillToEdit.id) {
        // Update an existing skill
        this.skillsService
          .updateSkill(this.skillToEdit)
          .subscribe({
            next: (updatedSkill) => {
              const index = this.skills.findIndex((r) => r.id === updatedSkill.id);
              this.skills[index] = this.skillAdapter.adapt(updatedSkill);
              this.skillToEdit = null;
              this.ShowForm = false;
              this.toastService.showSuccess('COMMON.UPDATE_SUCCESS');
            },
            error: (error) => {
              this.toastService.showSuccess('COMMON.UPDATE_ERROR');
            }
          });
      } else {
        // Add a new skill
        this.skillsService.addSkill(this.skillToEdit).subscribe({
          next: (newSkill) => {
            // this.skills.push(this.skillAdapter.adapt(newSkill));
            this.pagination.page = 1; // Reset to first page to see the new skill
            this.fetchSkills(); // Refresh the skills list
            this.skillToEdit = null;
            this.ShowForm = false;
            this.toastService.showSuccess('COMMON.UPDATE_SUCCESS');
          },
          error: (error) => {
            // TODO: translate error message
            this.errorMessage = 'Error during skill addition: ' + error.message;
            this.toastService.showError('COMMON.UPDATE_ERROR');
          }
        });
      }


    }
  }

  private deleteSkill(skill: Skill): void {
    this.skillsService.delete_skill(skill.id).subscribe({
      next: () => {
        // this.skills = this.skills.filter((s) => s.id !== skill.id);
        this.fetchSkills(); // Refresh the skills list after deletion
        this.toastService.showSuccess('COMMON.DELETE_SUCCESS');
      },
      error: (err) => {
        // TODO: translate error message use an {item} placeholder
        this.errorMessage = 'Error during skill deletion: ' + err.message;
        this.toastService.showError('COMMON.DELETE_ERROR');
      }
    });
  }

  trackById(index: number, skill: Skill): number {
    return skill.id;
  }
}
